import type { BaseAPIResponse } from '~/types/api';
import type { Company, CompanyAdvisory, DeletionGuideResponse } from '~/types/person/companies';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';

// Pure API service for company operations (non-public) - no state management
export const companiesService = {
    async getCompany(companyId: number): Promise<Company> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<Company>(nuxtApp.$api.routes.people.companies.get_company(companyId), {
            failure: 'Failed to fetch company',
        });
    },

    async getDeletionGuide(companyId: number): Promise<string> {
        const nuxtApp = useNuxtApp();

        const guide = await pbRequest<DeletionGuideResponse>(nuxtApp.$api.routes.companies.deletion_guide(companyId), {
            failure: 'Failed to fetch deletion guide',
        });

        return guide.guide;
    },

    async getCompanyAdvisories(companyId: number): Promise<CompanyAdvisory[]> {
        const nuxtApp = useNuxtApp();

        // A company with nothing to advise answers with an empty list, which is an answer.
        const envelope = await pbRequestRaw<BaseAPIResponse<CompanyAdvisory[]>>(
            nuxtApp.$api.routes.public.companies.advisories(companyId),
            { failure: 'Failed to fetch company advisories' }
        );

        return envelope.data ?? [];
    },

    async searchCompanies(query: string, per_page: number = 20, page: number = 1, type: string = ''): Promise<BaseAPIResponse<Company[]>> {
        const nuxtApp = useNuxtApp();

        // The whole envelope is the answer: callers page on its `meta`.
        return await pbRequestRaw<BaseAPIResponse<Company[]>>(
            nuxtApp.$api.routes.people.companies.search(query, per_page, page, type),
            { failure: 'Failed to search companies' }
        );
    },

    async requestNewCompany(name: string, url: string): Promise<void> {
        await pbRequestVoid('/companies/request', {
            method: 'POST',
            body: { name, url },
            expect: 201,
            failure: 'Failed to submit company request',
        });
    },
};
